import React, { ReactElement } from 'react'
import s from './ProductGallery.module.css'
import cn from 'classnames'
import type { Maybe, MediaGalleryInterface } from '@pylot-data/pylotschema'
import dynamic from 'next/dynamic'

const StackedGallery = dynamic(() => import('./StackedGallery'))
const ScrollGallery = dynamic(() => import('./ScrollGallery'))

export type ProductGalleryType = {
    mediaGalleryEntries: Maybe<MediaGalleryInterface>[]
    config?: { mobile?: galleryConfigType; desktop?: galleryConfigType }
}

export enum GalleryTypeEnum {
    SCROLL = 'SCROLL',
    STACKED = 'STACKED'
}

export type galleryConfigType = {
    gallerytype: string //type
    zoom: boolean
    fullscreen: boolean
    thumb?: boolean
    sliderProps?: {
        main?: {
            spaceBetween?: number
            navigation?: boolean
            pagination?:
                | boolean
                | {
                      clickable: boolean
                  }
        }
        thumbs?: {
            spaceBetween?: number
            slidesPerView?: number
        }
    }
    enableLastVideoAsFullWidth?: boolean
    imagesPerPage?: number
    loadMoreLabel?: string
    badgeLabel?: string
}

const defaultGalleryConfig = {
    mobile: {
        gallerytype: GalleryTypeEnum.SCROLL,
        zoom: true,
        fullscreen: false,
        thumb: false
    },
    desktop: {
        gallerytype: GalleryTypeEnum.STACKED,
        zoom: false,
        fullscreen: true
    }
}

export const ProductGallery = ({
    mediaGalleryEntries,
    config = defaultGalleryConfig
}: ProductGalleryType): ReactElement => {
    mediaGalleryEntries.sort((key1, key2) => {
        return key1!.position! - key2!.position!
    })
    const enabledMediaGalleryEntries = mediaGalleryEntries?.filter(
        (media) => !media?.disabled
    )
    const Gallery = (galleryConfig: galleryConfigType) => {
        return (
            <>
                {galleryConfig.gallerytype === GalleryTypeEnum.SCROLL && (
                    <div className={s['scroll-gallery']}>
                        <ScrollGallery
                            mediaGalleryEntries={enabledMediaGalleryEntries}
                            zoom={galleryConfig.zoom}
                            showFullscreen={galleryConfig.fullscreen}
                            thumb={galleryConfig.thumb}
                            sliderProps={galleryConfig.sliderProps}
                            badgeLabel={galleryConfig.badgeLabel}
                        />
                    </div>
                )}
                {galleryConfig.gallerytype === GalleryTypeEnum.STACKED && (
                    <div className={s['stacked-gallery']}>
                        <StackedGallery
                            mediaGalleryEntries={enabledMediaGalleryEntries}
                            zoom={galleryConfig.zoom}
                            showFullscreen={galleryConfig.fullscreen}
                            imagesPerPage={galleryConfig.imagesPerPage}
                            loadMoreLabel={galleryConfig.loadMoreLabel}
                            enableLastVideoAsFullWidth={
                                galleryConfig.enableLastVideoAsFullWidth
                            }
                            badgeLabel={galleryConfig.badgeLabel}
                        />
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={s['pdp-gallery']}>
            <div className={s['slider-container']}>
                {config.mobile && (
                    <div className={cn(s['mobile-gallery'])}>
                        {Gallery(config.mobile)}
                    </div>
                )}
                {config.desktop && (
                    <div className={cn(s['desktop-gallery'])}>
                        {Gallery(config.desktop)}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductGallery
